import { faCheck, faPencilAlt, faTimes } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Chip, Divider, Grid2, List, ListItem, ListItemButton, ListItemText, MenuItem, Paper, TextField, Tooltip, alpha, styled } from "@mui/material";
import { AuthenticatedUserContext, OnlinePatientManagementContext } from "@ngt/opms";
import { Dispatch, FC, Fragment, SetStateAction, createContext, memo, useContext, useEffect, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { Locale, Namespace, SaveTranslation, Translation } from "../../api/dtos";
import useLocales from "../../hooks/locales/useLocales";
import useNamespaces from "../../hooks/locales/useNamespaces";
import useTranslations from "../../hooks/locales/useTranslations";
import RolesContext from "@/contexts/RolesContext";
import Roles from "@/constants/Roles";

const StyledListItemButton = styled(ListItemButton)(({theme}) => ({
    ['&:first-of-type']: {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius
    },
    ['&:last-of-type']: {
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius
    },
    [`&.Mui-selected`]: {
        backgroundColor: alpha(theme.palette.primary.main, 0.25)
    }
}))

const BoxPreText = styled(Box)(({}) => ({
    whiteSpace: 'pre-wrap',
    flex: '1 1 auto'
}))

const BoxLocale = styled(Box)(({theme}) => ({
    background: theme.palette.grey[300],
    padding: theme.spacing(2),
    margin: theme.spacing(-1, 2, -1, -2),
    minWidth: 60,
    textAlign: 'center',
    alignSelf: 'stretch'
}))

const ChipState = styled(Chip)(({}) => ({
    alignSelf: 'flex-start',
    minWidth: 80,
    marginTop: 4
}))

const defaultLocale = 'en';

interface ITranslationContext {
    refresh: () => Promise<void>;
    update: (translation: Translation) => Promise<void>;
    // user: AuthenticateResponse | undefined;
    isSuperUser: boolean;
    isTranslator: boolean;
    edit: Record<number, Record<number, Record<string, { value?: string; loading: boolean }>>>;
    setEdit: Dispatch<SetStateAction<Record<number, Record<number, Record<string,  { value?: string; loading: boolean }>>>>>;
}

const TranslationContext = createContext<ITranslationContext>({
    refresh: async () => {},
    update: async (_: Translation) => {},
    isSuperUser: false,
    isTranslator: false,
    // user: undefined,
    edit: {},
    setEdit: (() => {}) as any
});

interface IStats {
    total: number;
    missing: number;
    valid: number;
    old: number;
}

interface ITranslation {
    translation?: Translation;
    stats: IStats;
}

interface ILocaleNamespace {
    namespace: Namespace;
    translations: Record<string, ITranslation>;
    stats: IStats;
}

interface ILocale {
    locale: Locale;
    namespaces: Record<string, ILocaleNamespace>;
    stats: IStats;
}

interface INamespace {
    namespace: Namespace;
    translationKeys: string[];
}

interface IResources {
    locales: Record<string, ILocale>;
    namespaces: INamespace[],
    stats: IStats;
}

const getResources = (locales: Locale[], namespaces: Namespace[], translations: Translation[]): IResources => {
    const resources: IResources = {
        locales: {},
        namespaces: [],
        stats: {
            missing: 0,
            total: 0,
            old: 0,
            valid: 0
        }
    };

    for (let locale of locales) {
        resources.locales[locale.code!] = {
            locale,
            namespaces: {

            },
            stats: {
                missing: 0,
                total: 0,
                old: 0,
                valid: 0
            }
        }
    }

    for (let namespace of namespaces) {
        const namespaceTranslations = translations.filter(x => x.namespaceId === namespace.id);

        const groupedNamespaceTranslations = namespaceTranslations.reduce<Record<string, { maxVersion: number, translations: Translation[] }>>((record, current) => {
            if (!record[current.key!]) {
                record[current.key!] = {
                    maxVersion: 1,
                    translations: []
                };
            }

            record[current.key!].translations.push(current);

            return record;
        }, {});

        const translationKeys = Object.keys(groupedNamespaceTranslations);

        for (let translationKey of translationKeys) {
            const maxVersion = groupedNamespaceTranslations[translationKey].translations.reduce<number>((max, current) => {
                const currentVersion = current.version ?? 1

                if (currentVersion > max) {
                    return currentVersion;
                }

                return max;
            }, 1)

            groupedNamespaceTranslations[translationKey].maxVersion = maxVersion;
        }

        
        resources.namespaces.push({
            namespace,
            translationKeys
        });

        for (let locale of locales) {
            const localeNamespace: ILocaleNamespace = {
                namespace,
                translations: translationKeys.reduce<Record<string, ITranslation>>((record, translationKey) => {
                    if (record[translationKey]) {
                        return record;
                    }

                    const groupedNamespaceTranslation = groupedNamespaceTranslations[translationKey];
                    const translation = groupedNamespaceTranslation.translations.find(x => x.localeId === locale.id);

                    record[translationKey] = {
                        translation,
                        stats: {
                            total: 1,
                            missing: !!translation ? 0 : 1,
                            valid: !!translation && (translation.version ?? 1) === groupedNamespaceTranslation.maxVersion ? 1 : 0,
                            old: !!translation && (translation.version ?? 1) !== groupedNamespaceTranslation.maxVersion ? 1 : 0
                        }
                    }

                    return record;
                }, {}),
                stats: {
                    missing: 0,
                    total: 0,
                    old: 0,
                    valid: 0
                }
            };

            for (let translationKey of translationKeys) {
                localeNamespace.stats.total += localeNamespace.translations[translationKey].stats.total;
                localeNamespace.stats.missing += localeNamespace.translations[translationKey].stats.missing;
                localeNamespace.stats.valid += localeNamespace.translations[translationKey].stats.valid;
                localeNamespace.stats.old += localeNamespace.translations[translationKey].stats.old;
            }

            resources.locales[locale.code!].namespaces[namespace.code!] = localeNamespace;
            resources.locales[locale.code!].stats.total += localeNamespace.stats.total;
            resources.locales[locale.code!].stats.missing += localeNamespace.stats.missing;
            resources.locales[locale.code!].stats.valid += localeNamespace.stats.valid;
            resources.locales[locale.code!].stats.old += localeNamespace.stats.old;

        }
    }

    for (let locale of locales) {
        
        resources.stats.total += resources.locales[locale.code!].stats.total;
        resources.stats.missing += resources.locales[locale.code!].stats.missing;
        resources.stats.valid += resources.locales[locale.code!].stats.valid;
        resources.stats.old += resources.locales[locale.code!].stats.old;
    }
    return resources;
}

const states = [
    {
        code: 'valid',
        name: 'Valid',
        color: 'success'
    },
    {
        code: 'old',
        name: 'Old',
        color: 'warning'
    },
    {
        code: 'missing',
        name: 'Missing',
        color: 'error'
    }
]


interface ITranslationsProps {
    selectedLocales: string[];
    translationKey: string;
    namespace: INamespace;
    resources: IResources;
    allResources: IResources;
}

const Translations: FC<ITranslationsProps> = ({
    namespace,
    resources,
    selectedLocales,
    translationKey,
    allResources
}) => {
    const defaultNamespace = allResources.locales[defaultLocale]?.namespaces[namespace.namespace.code!];
    const defaultTranslation: ITranslation | undefined = defaultNamespace.translations[translationKey];

    const chipState = useMemo(() => {
        let missing = 0;
        let old = 0;
        //let valid = 0;

        for (let locale of selectedLocales) {
            missing += resources.locales[locale]?.namespaces[namespace.namespace.code!].translations[translationKey].stats.missing;
            old += resources.locales[locale]?.namespaces[namespace.namespace.code!].translations[translationKey].stats.old;
            //valid += resources.locales[locale]?.namespaces[namespace.namespace.code!].translations[translationKey].stats.valid;
        }

          
        if (missing > 0) {
            return { 
                text: 'Missing',
                color: 'error'
            }
        } 
        else if (old > 0) {
            return { 
                text: 'Old',
                color: 'warning'
            }
        }
        else {
            return { 
                text: 'Valid',
                color: 'success'
            }
        }
    }, [translationKey, selectedLocales, resources, namespace])
    
    return (
        <Paper
            sx={{
                mb: 2
            }}
        >
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                {translationKey}
                <ChipState
                    label={chipState.text}
                    color={chipState.color as any}
                />
            </Box>
            <List
                disablePadding
            >
                <Divider />
                {
                    selectedLocales.map((selectedLocale, i) => {
                        const locale = resources.locales[selectedLocale]?.locale;
                        const translation: ITranslation | undefined = resources.locales[selectedLocale]?.namespaces[namespace.namespace.code!].translations[translationKey];

                        if (!translation) {
                            return (
                                <Fragment
                                    key={selectedLocale}
                                >
                                </Fragment>
                            )
                        }

                        return (
                            <Fragment
                                key={selectedLocale}
                            >
                                <TranslationView 
                                    defaultTranslation={defaultTranslation}
                                    locale={locale}
                                    namespace={namespace}
                                    resources={resources}
                                    selectedLocale={selectedLocale}
                                    translation={translation}
                                    translationKey={translationKey}
                                    allResources={allResources}
                                />
                                {i < (selectedLocales.length - 1) && <Divider />}
                            </Fragment>
                        )
                    })
                }
            </List>
        </Paper>
    )
};

interface ITranslationViewProps {
    selectedLocale: string;
    translationKey: string;
    defaultTranslation: ITranslation;
    translation: ITranslation;
    namespace: INamespace;
    resources: IResources;
    allResources: IResources;
    locale: Locale;
}

const TranslationView: FC<ITranslationViewProps> = ({
    defaultTranslation,
    selectedLocale,
    translation,
    namespace,
    translationKey,
    locale
}) => {
    const {isSuperUser, isTranslator, setEdit, edit, update} = useContext(TranslationContext);

    const editState = edit[locale.id!]?.[namespace.namespace.id!]?.[translationKey];
    const editValue = editState?.value;
    const isEdit = editValue !== undefined;
    const invalid = editValue === '';

    const canEdit = isTranslator && (isSuperUser || selectedLocale !== defaultLocale);

    return (
        <ListItem>
            <BoxLocale>
                {selectedLocale}
            </BoxLocale>
            {
                isEdit && (
                    <BoxPreText>
                        <TextField 
                            fullWidth
                            multiline
                            size="small"
                            value={editValue}
                            error={invalid}
                            disabled={editState.loading}
                            onChange={e => {
                                setEdit(current => {
                                    return {
                                        ...current,
                                        [locale.id!]: {
                                            ...current[locale.id!],
                                            [namespace.namespace.id!]: {
                                                ...current[locale.id!]?.[namespace.namespace.id!],
                                                [translationKey]: {
                                                    value: e.target.value ?? '',
                                                    loading: false
                                                }
                                            }
                                        }
                                    }
                                })
                            }}
                        />
                    </BoxPreText>
                )
            }
            {
                !isEdit && (
                    <BoxPreText>
                        {translation.translation?.value}
                    </BoxPreText>
                )
            }
            {
                isEdit && canEdit && (
                    <>
                        <Tooltip title={invalid ? "Provide a translation value to save" : "Save"}>
                            <div>
                                <LoadingButton
                                    disabled={invalid}
                                    loading={editState.loading}
                                    color="success"
                                    onClick={async () => {
                                        setEdit((current) => {
                                            return {
                                                ...current,
                                                [locale.id!]: {
                                                    ...current[locale.id!],
                                                    [namespace.namespace.id!]: {
                                                        ...current[locale.id!]?.[namespace.namespace.id!],
                                                        [translationKey]: {
                                                            value: editValue,
                                                            loading: true
                                                        }
                                                    }
                                                }
                                            }
                                        })

                                        const newData = new Translation({ ...translation.translation });

                                        newData.localeId = locale.id;
                                        newData.namespaceId = namespace.namespace.id;
                                        newData.key = translationKey;

                                        if ((translation.translation?.version ?? 0) > (defaultTranslation.translation?.version ?? 0)) {
                                            newData.version = translation.translation?.version ?? defaultTranslation.translation?.version ?? 1;
                                        }
                                        else {
                                            newData.version = defaultTranslation.translation?.version ?? translation.translation?.version ?? 1;
                                        }

                                        newData.value = editValue;

                                        await update(newData);

                                        setEdit((current) => {
                                            return {
                                                ...current,
                                                [locale.id!]: {
                                                    ...current[locale.id!],
                                                    [namespace.namespace.id!]: {
                                                        ...current[locale.id!]?.[namespace.namespace.id!],
                                                        [translationKey]: {
                                                            value: undefined,
                                                            loading: false
                                                        }
                                                    }
                                                }
                                            }
                                        })
                                    }}
                                >
                                    <FontAwesomeIcon fixedWidth icon={faCheck} />
                                </LoadingButton>
                            </div>
                        </Tooltip>
                        <Tooltip title="Cancel">
                            <LoadingButton
                                loading={editState.loading}
                                color="error"
                                onClick={() => {
                                    setEdit((current) => {
                                        return {
                                            ...current,
                                            [locale.id!]: {
                                                ...current[locale.id!],
                                                [namespace.namespace.id!]: {
                                                    ...current[locale.id!]?.[namespace.namespace.id!],
                                                    [translationKey]: {
                                                        value: undefined,
                                                        loading: false
                                                    }
                                                }
                                            }
                                        }
                                    })
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={faTimes} />
                            </LoadingButton>
                        </Tooltip>
                    </>
                )
            }
            {
                !isEdit && canEdit && (
                    <Tooltip title="Edit">
                        <Button
                            color="info"
                            onClick={() => {
                                setEdit((current) => {
                                    return {
                                        ...current,
                                        [locale.id!]: {
                                            ...current[locale.id!],
                                            [namespace.namespace.id!]: {
                                                ...current[locale.id!]?.[namespace.namespace.id!],
                                                [translationKey]: {
                                                    value: translation.translation?.value ?? '',
                                                    loading: false
                                                }
                                            }
                                        }
                                    }
                                })
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={faPencilAlt} />
                        </Button>
                    </Tooltip>
                )
            }
            {
                translation.stats.missing === 1 && (
                    <ChipState
                        label="Missing"
                        color="error"
                    />
                )
            }
            {
                translation.stats.old === 1 && (
                    <ChipState
                        label="Old"
                        color="warning"
                    />
                )
            }
            {
                translation.stats.valid === 1 && (
                    <ChipState
                        label="Valid"
                        color="success"
                    />
                )
            }
        </ListItem>
    );
}




interface IVirtuosoContext {
    selectedLocales: string[];
    namespace: INamespace;
    resources: IResources;
    allResources: IResources;
}

interface IVirtuosoInnerItem {
    index: number;
    item: string;
    context: IVirtuosoContext;
}


// Item contents are cached properly with React.memo
const InnerItem =({
    item,
    context
}: IVirtuosoInnerItem) => {
    return (
        <Translations
            {...context}
            translationKey={item}
        />
    )
};

const MemoizedInnerItem = memo(InnerItem) as typeof InnerItem;

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: string, context: IVirtuosoContext) => {
    return <MemoizedInnerItem index={index} item={item} context={context} />
}

interface INamespacesProps {
    selectedLocales: string[];
    resources: IResources;
    allResources: IResources;
}

const Namespaces: FC<INamespacesProps> = ({
    selectedLocales,
    resources,
    allResources
}) => {


    return (
        <Box>
            {
                resources.namespaces.map(x => (
                    <Box
                        key={x.namespace.code}
                    >
                        <Box
                            sx={{
                                mb: 1
                            }}
                        >
                            <strong>{x.namespace.name}</strong>
                        </Box>
                        {/* {
                            x.translationKeys.map(y => (
                                <Translations
                                    key={y}
                                    translationKey={y}
                                    namespace={x}
                                    resources={resources}
                                    selectedLocales={selectedLocales}
                                />
                            ))
                        } */}
                        <Virtuoso 
                            useWindowScroll
                            data={x.translationKeys}
                            context={{
                                selectedLocales,
                                resources,
                                namespace: x,
                                allResources: allResources
                            }}
                            itemContent={virtuosoItemContent}
                        />  
                    </Box>
                ))
            }
        </Box>
    )
}

const Locales: FC = () => {
    const { data: locales, loading: localesLoading, refresh: localesRefresh} = useLocales();
    const { data: namespaces, loading: namespacesLoading, refresh: namespacesRefresh } = useNamespaces();
    const { data: translations, loading: translationsLoading, refresh: translationsRefresh } = useTranslations();

    const [ edit, setEdit ] = useState<Record<number, Record<number, Record<string,  { value?: string; loading: boolean }>>>>({});
    const [ selectedLocales, setSelectedLocales ] = useState<string[] | undefined>(undefined);
    const [ selectedStates, setSelectedStates ] = useState<string[] | undefined>(['missing', 'old', 'valid']);
    const [ selectedNamespaces, setSelectedNamespaces ] = useState<string[] | undefined>(undefined);
    const [ search, setSearch ] = useState<string | undefined>(undefined);
    const [ searchType, setSearchType ] = useState<number>(1);

    
    const [allResources, setAllResources] = useState<IResources | undefined>(undefined);
    const [resources, setResources] = useState<IResources | undefined>(undefined);
    const { serviceStackClient } = useContext(OnlinePatientManagementContext);
    
    const { data: user } = useContext(AuthenticatedUserContext);
    const { data: roles } = useContext(RolesContext);

    const loading = namespacesLoading || localesLoading || translationsLoading || !resources;

    const translationContext = useMemo(() => {
        return {
            refresh: async () => {
                await localesRefresh();
                await namespacesRefresh();
                await translationsRefresh();
            },
            update: async (translation: Translation) => {
                

                await serviceStackClient.post(new SaveTranslation({
                    translation: translation
                }));
                
                await localesRefresh();
                await namespacesRefresh();
                await translationsRefresh();
            },
            user: user,
            isSuperUser: !!roles?.includes(Roles.SUPER_USER),
            isTranslator: !!roles?.includes(Roles.SUPER_USER) || !!roles?.includes(Roles.TRANSLATOR),
            edit: edit,
            setEdit: setEdit
        }
    }, [localesRefresh, namespacesRefresh, translationsRefresh, user, edit, setEdit, serviceStackClient])
    

    useEffect(() => {
        setSelectedLocales(x => {
            if (x === undefined) {
                return locales?.map(x => x.code!);
            }

            return x
        })
    }, [locales, setSelectedLocales]);

    useEffect(() => {
        setSelectedNamespaces(x => {
            if (x === undefined) {
                return namespaces?.map(x => x.code!);
            }

            return x
        })
    }, [namespaces, setSelectedNamespaces]);

    useEffect(() => {
        if (locales && namespaces && translations) {
            setAllResources(getResources(locales, namespaces, translations));
        }
        else {
            setAllResources(undefined);
        }
    }, [locales, namespaces, translations])

    useEffect(() => {
        if (allResources) {
            if (!search && selectedLocales?.length === locales?.length && selectedStates?.length === 3) {
                setResources(allResources);
            }

            let newResources = allResources;
            let adjustStats = false;

            if (selectedLocales?.length !== locales?.length) {
                const localeLimitedResources: IResources = {
                    locales: {},
                    namespaces: [...newResources.namespaces],
                    stats: {
                        missing: 0,
                        old: 0,
                        total: 0,
                        valid: 0
                    }
                };
    
                for (let localeKey in newResources.locales ) {
                    if (selectedLocales?.includes(localeKey)) {
                        localeLimitedResources.locales[localeKey] = newResources.locales[localeKey]
                    }
                }

                newResources = localeLimitedResources;
                adjustStats = true;
            }

            if (selectedNamespaces?.length !== namespaces?.length) {
                const namespaceLimitedResources: IResources = {
                    locales: {},
                    namespaces: newResources.namespaces.filter(x => selectedNamespaces?.includes(x.namespace.code!)),
                    stats: {
                        missing: 0,
                        old: 0,
                        total: 0,
                        valid: 0
                    }
                };
    
                for (let localeKey in newResources.locales ) {
                    const limitedNamespaces: Record<string, ILocaleNamespace> = {};
                    

                    for (let namespaceKey in newResources.locales[localeKey].namespaces ) {
                        const localeNamespace = newResources.locales[localeKey].namespaces[namespaceKey];
                        if (selectedNamespaces?.includes(localeNamespace.namespace.code!)) {
                            limitedNamespaces[namespaceKey] = localeNamespace;
                        }
                    }

                    namespaceLimitedResources.locales[localeKey] = {
                        locale: newResources.locales[localeKey].locale,
                        namespaces: limitedNamespaces,
                        stats: {
                            missing: 0,
                            old: 0,
                            total: 0,
                            valid: 0
                        }
                    }
                }

                newResources = namespaceLimitedResources;
                adjustStats = true;
            }

            

            if (search) {
                const searchToUse = search.toLowerCase();

                const searchLimitedResources: IResources = {
                    locales: {},
                    namespaces: [],
                    stats: {
                        missing: 0,
                        old: 0,
                        total: 0,
                        valid: 0
                    }
                };

                for (let namespace of newResources.namespaces) {
                    searchLimitedResources.namespaces.push({
                        namespace: namespace.namespace,
                        translationKeys: Array.from(new Set(
                            translations!
                                .filter(x => x.namespaceId === namespace.namespace.id)
                                .filter(x => 
                                    (searchType != 3 && x.key?.toLowerCase().includes(searchToUse)) || 
                                    (searchType != 2 && x.value?.toLowerCase().includes(searchToUse)))
                                .map(x => x.key!)
                        ))
                    })
                }
    
                for (let localeKey in newResources.locales ) {
                    const namespaces: Record<string, ILocaleNamespace> = {};
                    

                    for (let namespaceKey in newResources.locales[localeKey].namespaces ) {
                        const namespace = searchLimitedResources.namespaces.find(x => x.namespace.code === namespaceKey);
                        const localeNamespace = newResources.locales[localeKey].namespaces[namespaceKey];

                        const limitedTranslations: Record<string, ITranslation> = {};

                        for (let translationKey in newResources.locales[localeKey].namespaces[namespaceKey].translations) {
                            if (!namespace?.translationKeys.includes(translationKey)) {
                                continue;
                            }

                            limitedTranslations[translationKey] = newResources.locales[localeKey].namespaces[namespaceKey].translations[translationKey];
                        } 


                        namespaces[namespaceKey] = {
                            namespace: localeNamespace.namespace,
                            translations: limitedTranslations,
                            stats: {
                                missing: 0,
                                old: 0,
                                total: 0,
                                valid: 0
                            }
                        }
                    }

                    searchLimitedResources.locales[localeKey] = {
                        locale: newResources.locales[localeKey].locale,
                        namespaces: namespaces,
                        stats: {
                            missing: 0,
                            old: 0,
                            total: 0,
                            valid: 0
                        }
                    }
                }

                newResources = searchLimitedResources;
                adjustStats = true;
            }

            if (selectedStates?.length !== 3) {

                const stateLimitedResources: IResources = {
                    locales: {},
                    namespaces: [],
                    stats: {
                        missing: 0,
                        old: 0,
                        total: 0,
                        valid: 0
                    }
                };

                const includeMissing = selectedStates?.includes('missing');
                const includeOld = selectedStates?.includes('old');
                const includeValid = selectedStates?.includes('valid');

                for (let namespace of newResources.namespaces) {
                    const translationKeys = Array.from(new Set(
                        namespace.translationKeys.filter(translationKey => {
                            const translationKeyState = Object.keys(newResources.locales).reduce<{ missing: number, valid: number, old: number}>((state, localeKey) => {
                                const translation = newResources.locales[localeKey].namespaces[namespace.namespace.code!].translations[translationKey];

                                state.missing += translation.stats.missing;
                                state.valid += translation.stats.valid;
                                state.old += translation.stats.old;

                                return state;
                            }, {
                                missing: 0,
                                valid: 0,
                                old: 0
                            });

                            if (includeMissing && translationKeyState.missing > 0) {
                                return true;
                            }

                            if (includeOld && translationKeyState.old > 0 && translationKeyState.missing === 0) {
                                return true;
                            }

                            if (includeValid && translationKeyState.valid > 0  && translationKeyState.old === 0 && translationKeyState.missing === 0) {
                                return true;
                            }

                            return false;
                        })
                    ))

                    stateLimitedResources.namespaces.push({
                        namespace: namespace.namespace,
                        translationKeys: translationKeys
                    })
                }
    
                for (let localeKey in newResources.locales ) {
                    const namespaces: Record<string, ILocaleNamespace> = {};
                    

                    for (let namespaceKey in newResources.locales[localeKey].namespaces ) {
                        const namespace = stateLimitedResources.namespaces.find(x => x.namespace.code === namespaceKey);
                        const localeNamespace = newResources.locales[localeKey].namespaces[namespaceKey];

                        const limitedTranslations: Record<string, ITranslation> = {};

                        for (let translationKey in newResources.locales[localeKey].namespaces[namespaceKey].translations) {
                            if (!namespace?.translationKeys.includes(translationKey)) {
                                continue;
                            }

                            limitedTranslations[translationKey] = newResources.locales[localeKey].namespaces[namespaceKey].translations[translationKey];
                        } 


                        namespaces[namespaceKey] = {
                            namespace: localeNamespace.namespace,
                            translations: limitedTranslations,
                            stats: {
                                missing: 0,
                                old: 0,
                                total: 0,
                                valid: 0
                            }
                        }
                    }

                    stateLimitedResources.locales[localeKey] = {
                        locale: newResources.locales[localeKey].locale,
                        namespaces: namespaces,
                        stats: {
                            missing: 0,
                            old: 0,
                            total: 0,
                            valid: 0
                        }
                    }
                }
    

                newResources = stateLimitedResources;
                adjustStats = true;
            }

            if (adjustStats) {
                const locales: Record<string, ILocale> = {};

                let resourceMissing = 0;
                let resourceValid = 0;
                let resourceOld = 0;

                for (let localeKey in newResources.locales ) {
                    const namespaces: Record<string, ILocaleNamespace> = {};
                    
                    let localeMissing = 0;
                    let localeValid = 0;
                    let localeOld = 0;

                    for (let namespaceKey in newResources.locales[localeKey].namespaces ) {
                        const localeNamespace = newResources.locales[localeKey].namespaces[namespaceKey];

                        const translations: Record<string, ITranslation> = {};

                        let namespaceMissing = 0;
                        let namespaceValid = 0;
                        let namespaceOld = 0;

                        for (let translationKey in newResources.locales[localeKey].namespaces[namespaceKey].translations) {
                            const t = newResources.locales[localeKey].namespaces[namespaceKey].translations[translationKey];

                            translations[translationKey] = t; 
                            namespaceMissing += t.stats.missing;
                            namespaceValid += t.stats.valid;
                            namespaceOld += t.stats.old;
                        } 


                        namespaces[namespaceKey] = {
                            namespace: localeNamespace.namespace,
                            translations: translations,
                            stats: {
                                missing: namespaceMissing,
                                old: namespaceOld,
                                total: namespaceMissing + namespaceOld + namespaceValid,
                                valid: namespaceValid
                            }
                        }

                        localeMissing += namespaceMissing;
                        localeValid += namespaceValid;
                        localeOld += namespaceOld;
                    }

                    locales[localeKey] = {
                        locale: newResources.locales[localeKey].locale,
                        namespaces: namespaces,
                        stats: {
                            missing: localeMissing,
                            old: localeOld,
                            total: localeMissing + localeOld + localeValid,
                            valid: localeValid
                        }
                    }

                    resourceMissing += localeMissing;
                    resourceValid += localeValid;
                    resourceOld += localeOld;
                }

                newResources = {
                    locales: locales,
                    namespaces: newResources.namespaces,
                    stats: {
                        missing: resourceMissing,
                        old: resourceOld,
                        total: resourceMissing + resourceOld + resourceValid,
                        valid: resourceValid
                    }
                };
            }

            setResources(newResources);
        }
        else {
            setResources(undefined);
        }
    }, [selectedLocales, allResources, search, searchType, selectedStates, selectedNamespaces, locales, namespaces, translations]);

    if (loading) {
        return (
            <>
                Loading...
            </>
        )
    }
    
    return (
        <TranslationContext.Provider value={translationContext}>
            <Grid2
                container
            >
                <Grid2
                    size={{
                        xs: 12,
                        md: 4,
                        lg: 2
                    }}
                    sx={{
                        p: 2
                    }}
                >
                    <Box
                        sx={{
                            mb: 2
                        }}
                    >
                        <strong>Locales</strong>
                        <Paper
                            sx={{
                                mt: 1
                            }}
                        >
                            <List
                                disablePadding
                            >
                                {
                                    locales?.map((x, i) => (
                                        <Fragment
                                            key={x.code}
                                        >
                                            <StyledListItemButton
                                                selected={selectedLocales?.includes(x.code!)}
                                                onClick={() => {
                                                    setSelectedLocales(c => {
                                                        if (c?.includes(x.code!)) {
                                                            return c.filter(y => y !== x.code);
                                                        }

                                                        return [...(c ?? []), x.code!]
                                                    })
                                                }}
                                            >
                                                <ListItemText
                                                    primary={x.name}
                                                    secondary={x.code}
                                                />
                                            </StyledListItemButton>
                                            {i < (locales.length - 1) && <Divider />}
                                        </Fragment>
                                    ))
                                }
                            </List>
                        </Paper>
                    </Box>
                    <Box
                        sx={{
                            mb: 2
                        }}
                    >
                        <strong>Namespaces</strong>
                        <Paper
                            sx={{
                                mt: 1
                            }}
                        >
                            <List
                                disablePadding
                            >
                                {
                                    namespaces?.map((x, i) => (
                                        <Fragment
                                            key={x.code}
                                        >
                                            <StyledListItemButton
                                                selected={selectedNamespaces?.includes(x.code!)}
                                                onClick={() => {
                                                    setSelectedNamespaces(c => {
                                                        if (c?.includes(x.code!)) {
                                                            return c.filter(y => y !== x.code);
                                                        }

                                                        return [...(c ?? []), x.code!]
                                                    })
                                                }}
                                            >
                                                <ListItemText
                                                    primary={x.name}
                                                    secondary={x.code}
                                                />
                                            </StyledListItemButton>
                                            {i < (namespaces.length - 1) && <Divider />}
                                        </Fragment>
                                    ))
                                }
                            </List>
                        </Paper>
                    </Box>
                    <Box
                        sx={{
                            mb: 2
                        }}
                    >
                        <strong>States</strong>
                        <Paper
                            sx={{
                                mt: 1
                            }}
                        >
                            <List
                                disablePadding
                            >
                                {
                                    states.map((x, i) => (
                                        <Fragment
                                            key={x.code}
                                        >
                                            
                                            <StyledListItemButton
                                                selected={selectedStates?.includes(x.code!)}
                                                onClick={() => {
                                                    setSelectedStates(c => {
                                                        if (c?.includes(x.code!)) {
                                                            return c.filter(y => y !== x.code);
                                                        }

                                                        return [...(c ?? []), x.code!]
                                                    })
                                                }}
                                            >
                                                <ListItemText
                                                    primary={x.name}
                                                />
                                                {
                                                    (resources.stats as any)[x.code] !== (allResources!.stats as any)[x.code] ? 
                                                        (
                                                            <Chip color={x.color as any} label={`${(resources.stats as any)[x.code]} / ${(allResources!.stats as any)[x.code]}`} />
                                                        ) :
                                                        (
                                                            <Chip color={x.color as any} label={(resources.stats as any)[x.code]} />
                                                        )
                                                }
                                            </StyledListItemButton>
                                            {i < (['valid', 'old', 'missing'].length - 1) && <Divider />}
                                        </Fragment>
                                    ))
                                }
                            </List>
                        </Paper>
                    </Box>
                    <Box sx={{mt: 2}}>
                        <strong>Search</strong>
                        <TextField 
                            label="Type"
                            margin="normal"
                            select
                            fullWidth
                            value={searchType ?? ''}
                            onChange={(e) => {
                                if (e.target.value === '') {
                                    setSearchType(1);
                                    return;
                                }

                                setSearchType(+e.target.value)
                            }}
                        >
                            <MenuItem value={1}>Key &amp; Value</MenuItem>
                            <MenuItem value={2}>Key</MenuItem>
                            <MenuItem value={3}>Value</MenuItem>
                        </TextField>
                        <TextField 
                            margin="normal"
                            label="Query" 
                            fullWidth
                            value={search ?? ''}
                            onChange={(e) => {
                                if (e.target.value === '') {
                                    setSearch(undefined);
                                    return;
                                }

                                setSearch(e.target.value)
                            }}
                        />
                    </Box>
                </Grid2>
                <Grid2
                    size={{
                        xs: 12,
                        md: 8,
                        lg: 10
                    }}
                    sx={{
                        p: 2
                    }}
                >
                    {selectedLocales && <Namespaces selectedLocales={selectedLocales} allResources={allResources!} resources={resources} />}
                </Grid2>
            </Grid2>
        </TranslationContext.Provider>
    );
}

export default Locales